import React, { useMemo } from "react";

interface IKeyMetricsDataPoint {
  store_name?: string;
  city?: string;
  plastic_weight_per_week: number;
  total_statie_items_per_week: number;
  plastic_droppers_per_week: number;
  cumulative_droppers: number;
  cumulative_plastic_droppers: number;
  cumulative_new_active_plastic_droppers: number;
  months_since_opening?: number; // Months since opening from backend
  period: string;
  date?: string;
  week_start?: string;
  month_start?: string;
  year?: number;
}

interface KeyMetricsTableProps {
  data: IKeyMetricsDataPoint[] | null;
  isLoading: boolean;
  type: "city" | "location";
  period: "weekly" | "monthly" | "quarterly" | "yearly";
}

// Location mapping for display names
const LOCATION_DISPLAY_NAMES: Record<string, string> = {
  "001_VLS_Ams_FS": "VLS (020) - FS",
  "003_BOLO_Ams_SS": "BoLo (020) - SS",
  "004_SMA_Utr_FS": "SMA (030) - FS",
  "005_BSM_Ams_C": "BSM (020) - C",
};

// Opening dates (hardcoded for now, can be moved to backend later)
const OPENING_DATES: Record<string, { date: string; target: number }> = {
  "001_VLS_Ams_FS": { date: "Aug '24", target: 780 },
  "003_BOLO_Ams_SS": { date: "Mrt '25", target: 600 },
  "004_SMA_Utr_FS": { date: "Apr '25", target: 1100 },
  "005_BSM_Ams_C": { date: "Jun '25", target: 500 },
};

const CITY_OPENING_DATES: Record<string, { date: string; target: number }> = {
  Amsterdam: { date: "Aug '24", target: 1380 },
  Utrecht: { date: "Apr '25", target: 1100 },
  "Den Haag": { date: "TBD", target: 0 },
  Rotterdam: { date: "TBD", target: 0 },
};

// Number of stores per city
const STORES_PER_CITY: Record<string, number> = {
  Amsterdam: 4,
  Utrecht: 1,
  "Den Haag": 4,
  Rotterdam: 1,
};

export const KeyMetricsTable = React.memo(
  ({ data, isLoading, type, period }: KeyMetricsTableProps) => {
    const processedData = useMemo(() => {
      if (!data || data.length === 0) return null;

      // Group data by location/city and calculate averages
      const grouped: Record<
        string,
        {
          name: string;
          plastic_weight_per_week: number[];
          total_statie_items_per_week: number[];
          plastic_droppers_per_week: number[];
          cumulative_droppers: number[];
          cumulative_plastic_droppers: number[];
          cumulative_new_active_plastic_droppers: number[];
          months_since_opening: number | null; // Store months_since_opening from backend
        }
      > = {};

      data.forEach((item) => {
        const key = type === "location" ? item.store_name : item.city;
        if (!key) return;

        if (!grouped[key]) {
          grouped[key] = {
            name: key,
            plastic_weight_per_week: [],
            total_statie_items_per_week: [],
            plastic_droppers_per_week: [],
            cumulative_droppers: [],
            cumulative_plastic_droppers: [],
            cumulative_new_active_plastic_droppers: [],
            months_since_opening: item.months_since_opening ?? null,
          };
        }

        grouped[key].plastic_weight_per_week.push(
          item.plastic_weight_per_week || 0
        );
        grouped[key].total_statie_items_per_week.push(
          item.total_statie_items_per_week || 0
        );
        grouped[key].plastic_droppers_per_week.push(
          item.plastic_droppers_per_week || 0
        );
        grouped[key].cumulative_droppers.push(item.cumulative_droppers || 0);
        grouped[key].cumulative_plastic_droppers.push(
          item.cumulative_plastic_droppers || 0
        );
        grouped[key].cumulative_new_active_plastic_droppers.push(
          item.cumulative_new_active_plastic_droppers || 0
        );
        // Update months_since_opening if available (use first non-null value)
        if (
          item.months_since_opening !== undefined &&
          item.months_since_opening !== null
        ) {
          grouped[key].months_since_opening = item.months_since_opening;
        }
      });

      // Calculate averages and max values
      const result = Object.values(grouped).map((group) => {
        const avg = (arr: number[]) =>
          arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
        const max = (arr: number[]) => (arr.length > 0 ? Math.max(...arr) : 0);

        const storeCount =
          type === "city" ? STORES_PER_CITY[group.name] || 1 : 1;
        return {
          name: group.name,
          displayName:
            type === "location"
              ? LOCATION_DISPLAY_NAMES[group.name] || group.name
              : `${group.name} (${storeCount})`,
          storeCount,
          plastic_weight_per_week: avg(group.plastic_weight_per_week),
          total_statie_items_per_week: avg(group.total_statie_items_per_week),
          plastic_droppers_per_week: avg(group.plastic_droppers_per_week),
          cumulative_droppers: max(group.cumulative_droppers),
          cumulative_plastic_droppers: max(group.cumulative_plastic_droppers),
          cumulative_new_active_plastic_droppers: max(
            group.cumulative_new_active_plastic_droppers
          ),
          months_since_opening: group.months_since_opening,
        };
      });

      return result.sort((a, b) => a.name.localeCompare(b.name));
    }, [data, type]);

    const getMonthsSinceOpening = (item: {
      months_since_opening: number | null | undefined;
      name: string;
    }): number => {
      // First try to get from backend data
      if (
        item.months_since_opening !== null &&
        item.months_since_opening !== undefined
      ) {
        return item.months_since_opening;
      }

      // Fallback to hardcoded calculation if backend data not available
      const openingInfo =
        type === "location"
          ? OPENING_DATES[item.name]
          : CITY_OPENING_DATES[item.name];
      if (!openingInfo || openingInfo.date === "TBD") return 0;

      // Parse opening date (e.g., "Aug '24" or "Mrt '25")
      const monthMap: Record<string, number> = {
        Jan: 0,
        Feb: 1,
        Mrt: 2,
        Mar: 2,
        Apr: 3,
        May: 4,
        Jun: 5,
        Jul: 6,
        Aug: 7,
        Sep: 8,
        Okt: 9,
        Oct: 9,
        Nov: 10,
        Dec: 11,
      };

      const parts = openingInfo.date.split(" ");
      const monthStr = parts[0];
      const yearStr = parts[1]?.replace("'", "");
      const month = monthMap[monthStr] ?? 0;
      const year = parseInt(`20${yearStr}`) || new Date().getFullYear();

      const openingDate = new Date(year, month, 1);
      const now = new Date();
      const monthsDiff =
        (now.getFullYear() - openingDate.getFullYear()) * 12 +
        (now.getMonth() - openingDate.getMonth());

      return Math.max(0, monthsDiff);
    };

    const getOpeningInfo = (key: string) => {
      const openingInfo =
        type === "location" ? OPENING_DATES[key] : CITY_OPENING_DATES[key];
      if (!openingInfo) return { date: "TBD", target: 0 };
      return openingInfo;
    };

    if (isLoading) {
      return (
        <div className="min-h-[400px] flex items-center justify-center">
          <div className="text-gray-500">Loading...</div>
        </div>
      );
    }

    if (!processedData || processedData.length === 0) {
      return (
        <div className="min-h-[400px] flex items-center justify-center">
          <div className="text-gray-500">No data available</div>
        </div>
      );
    }

    return (
      <div className="w-full overflow-x-auto">
        <table className="w-full border-collapse border border-black">
          <thead>
            {/* Header row with location/city names */}
            <tr style={{ backgroundColor: "#D2D2B2" }}>
              <th
                className="border border-black p-2 text-left font-bold"
                style={{ backgroundColor: "#D2D2B2" }}
              >
                Metric
              </th>
              {processedData.map((item) => (
                <th
                  key={item.name}
                  className="border border-black p-2 text-center font-bold"
                  style={{ backgroundColor: "#D2D2B2" }}
                >
                  <div>{item.displayName}</div>
                </th>
              ))}
            </tr>
            {/* Opening row */}
            <tr style={{ backgroundColor: "#A59B5A" }}>
              <td
                className="border border-black p-2 font-semibold"
                style={{ backgroundColor: "#A59B5A" }}
              >
                Opening
              </td>
              {processedData.map((item) => {
                if (type === "city") {
                  return (
                    <td
                      key={`opening-${item.name}`}
                      className="border border-black p-2 text-center"
                      style={{ backgroundColor: "#A59B5A" }}
                    >
                      n.v.t.
                    </td>
                  );
                }
                const openingInfo = getOpeningInfo(item.name);
                const monthsSince = getMonthsSinceOpening(item);
                return (
                  <td
                    key={`opening-${item.name}`}
                    className="border border-black p-2 text-center"
                    style={{ backgroundColor: "#A59B5A" }}
                  >
                    {openingInfo.date} | {monthsSince} mnd
                  </td>
                );
              })}
            </tr>
            {/* Monthly target row */}
            <tr style={{ backgroundColor: "#D2D2B2" }}>
              <td
                className="border border-black p-2 font-semibold"
                style={{ backgroundColor: "#D2D2B2" }}
              >
                Maand target per eind dec '26
              </td>
              {processedData.map((item) => {
                if (type === "city") {
                  return (
                    <td
                      key={`target-${item.name}`}
                      className="border border-black p-2 text-center"
                      style={{ backgroundColor: "#D2D2B2" }}
                    >
                      n.v.t.
                    </td>
                  );
                }
                const openingInfo = getOpeningInfo(item.name);
                return (
                  <td
                    key={`target-${item.name}`}
                    className="border border-black p-2 text-center"
                    style={{ backgroundColor: "#D2D2B2" }}
                  >
                    {openingInfo.target.toLocaleString()}
                  </td>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {/* Row 1: Plastic kg p.mnd */}
            <tr>
              <td className="border border-black p-2 font-semibold">
                Plastic kg p.mnd (gemiddeld per locatie)
              </td>
              {processedData.map((item) => {
                const avgPerStore =
                  type === "city" && item.storeCount > 1
                    ? Math.round(item.plastic_weight_per_week / item.storeCount)
                    : null;
                return (
                  <td
                    key={`plastic-kg-${item.name}`}
                    className="border border-black p-2 text-center"
                  >
                    <div>
                      {Math.round(
                        item.plastic_weight_per_week
                      ).toLocaleString()}
                    </div>
                    {avgPerStore !== null && (
                      <div className="text-sm text-gray-600">
                        ({avgPerStore.toLocaleString()} per store)
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
            {/* Row 2: Statie items p.mnd */}
            <tr>
              <td className="border border-black p-2 font-semibold">
                Statie items p.mnd (gemiddeld per locatie)
              </td>
              {processedData.map((item) => {
                const avgPerStore =
                  type === "city" && item.storeCount > 1
                    ? Math.round(
                        item.total_statie_items_per_week / item.storeCount
                      )
                    : null;
                return (
                  <td
                    key={`statie-${item.name}`}
                    className="border border-black p-2 text-center"
                  >
                    <div>
                      {Math.round(
                        item.total_statie_items_per_week
                      ).toLocaleString()}
                    </div>
                    {avgPerStore !== null && (
                      <div className="text-sm text-gray-600">
                        ({avgPerStore.toLocaleString()} per store)
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
            {/* Row 3: Plastic Droppers p.mnd */}
            <tr>
              <td className="border border-black p-2 font-semibold">
                Plastic Droppers p.mnd (gemiddeld per locatie)
              </td>
              {processedData.map((item) => {
                const avgPerStore =
                  type === "city" && item.storeCount > 1
                    ? Math.round(
                        item.plastic_droppers_per_week / item.storeCount
                      )
                    : null;
                return (
                  <td
                    key={`plastic-droppers-${item.name}`}
                    className="border border-black p-2 text-center"
                  >
                    <div>
                      {Math.round(
                        item.plastic_droppers_per_week
                      ).toLocaleString()}
                    </div>
                    {avgPerStore !== null && (
                      <div className="text-sm text-gray-600">
                        ({avgPerStore.toLocaleString()} per store)
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
            {/* Row 4: Droppers (cumulatief) */}
            <tr>
              <td className="border border-black p-2 font-semibold">
                Droppers (cumulatief) (gemiddeld per locatie)
              </td>
              {processedData.map((item) => {
                const avgPerStore =
                  type === "city" && item.storeCount > 1
                    ? Math.round(item.cumulative_droppers / item.storeCount)
                    : null;
                return (
                  <td
                    key={`cumulative-droppers-${item.name}`}
                    className="border border-black p-2 text-center"
                  >
                    <div>{item.cumulative_droppers.toLocaleString()}</div>
                    {avgPerStore !== null && (
                      <div className="text-sm text-gray-600">
                        ({avgPerStore.toLocaleString()} per store)
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
            {/* Row 5: Plastic Droppers (cumulatief) | %} */}
            <tr>
              <td className="border border-black p-2 font-semibold">
                Plastic Droppers (cumulatief) | % (gemiddeld per locatie)
              </td>
              {processedData.map((item) => {
                const percentage =
                  item.cumulative_droppers > 0
                    ? (
                        (item.cumulative_plastic_droppers /
                          item.cumulative_droppers) *
                        100
                      ).toFixed(1)
                    : "0.0";
                const avgPerStore =
                  type === "city" && item.storeCount > 1
                    ? Math.round(
                        item.cumulative_plastic_droppers / item.storeCount
                      )
                    : null;
                return (
                  <td
                    key={`cumulative-plastic-${item.name}`}
                    className="border border-black p-2 text-center"
                  >
                    <div>
                      {item.cumulative_plastic_droppers.toLocaleString()} |{" "}
                      {percentage}%
                    </div>
                    {avgPerStore !== null && (
                      <div className="text-sm text-gray-600">
                        ({avgPerStore.toLocaleString()} per store)
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
            {/* Row 6: Active Plastic Droppers (cumulatief) | %} */}
            <tr>
              <td className="border border-black p-2 font-semibold">
                Active Plastic Droppers (cumulatief) | % (gemiddeld per locatie)
              </td>
              {processedData.map((item) => {
                const percentage =
                  item.cumulative_droppers > 0
                    ? (
                        (item.cumulative_new_active_plastic_droppers /
                          item.cumulative_droppers) *
                        100
                      ).toFixed(1)
                    : "0.0";
                const avgPerStore =
                  type === "city" && item.storeCount > 1
                    ? Math.round(
                        item.cumulative_new_active_plastic_droppers /
                          item.storeCount
                      )
                    : null;
                return (
                  <td
                    key={`active-plastic-${item.name}`}
                    className="border border-black p-2 text-center"
                  >
                    <div>
                      {item.cumulative_new_active_plastic_droppers.toLocaleString()}{" "}
                      | {percentage}%
                    </div>
                    {avgPerStore !== null && (
                      <div className="text-sm text-gray-600">
                        ({avgPerStore.toLocaleString()} per store)
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    );
  }
);

KeyMetricsTable.displayName = "KeyMetricsTable";
